/**
 * PATH: apps/billing/src/hooks/useBills.ts
 * FILE_ID: BILLING-HOOKS-USEBILLS-002
 * PURPOSE: TanStack Query wrappers around the production /api/bills/* endpoints.
 * WHY: apps/billing's pages were rendering mock arrays. The production backend
 *      has had a complete bills API since `organisation/views/bill_views/`
 *      shipped — wiring these hooks turns apps/billing into a hub showing
 *      real data for the logged-in tenant.
 *
 * FLOW (URLs imported from `@quotech/bills/endpoints` so they cannot drift
 * from the backend; types imported from `@quotech/bills/types` so the row
 * shape matches `BillListSerializer` / `BillDetailSerializer` in lockstep
 * with apps/sam):
 *
 *   useBills(filter)         -> GET   billsEndpoints.list(filter)
 *   useBill(id)              -> GET   billsEndpoints.detail(id)
 *   useBillStats()           -> GET   billsEndpoints.stats()
 *   useExtractBill()         -> POST  billsEndpoints.extractFromEmail(emailId)
 *   useExtractPendingBills() -> POST  billsEndpoints.extractPending()
 *   useUpdateBill()          -> PATCH billsEndpoints.update(id)
 *   useApproveBill()         -> POST  billsEndpoints.approve(id)
 *   useRejectBill()          -> POST  billsEndpoints.reject(id)
 *   useLinkBillToJob()       -> POST  billsEndpoints.linkJob(id)
 *   useXeroPushBill()        -> POST  billsEndpoints.pushToXero(id)
 *
 * DEPENDENCIES:
 *   @quotech/api:   shared axios client + useApiQuery (auth + tenant headers
 *                   are injected automatically; do not roll your own fetch).
 *                   useApiMutation is hard-coded to a single URL so we use
 *                   raw `useMutation` for path-parameterised actions.
 *   @quotech/bills: Phase B shared package — types + endpoint constants only.
 *                   UI components stay app-local until Phase C.
 *
 * RELATED:
 *   - packages/bills/src/types.ts:     authoritative type contracts.
 *   - packages/bills/src/endpoints.ts: authoritative URL strings.
 *   - apps/sam/src/features/bills/hooks/useBills.ts: SAM's parallel hook layer
 *     (uses fetch + vanilla state instead of axios + TanStack Query).
 */

import {
  useApiClient,
  useApiQuery,
  useInvalidateQueries,
  useMutation,
} from '@quotech/api';
import {
  billsEndpoints,
  type BillStatus,
  type BillDetail,
  type BillsListResponse,
  type BillStats,
  type BillEditablePatch,
  type ExtractPendingResponse,
} from '@quotech/bills';

// Re-export the shared types so existing imports across apps/billing
// (`import type { BillListRow } from '../hooks/useBills'`) keep working
// without forcing a churn-only edit through every page in this MR.
export type {
  BillStatus,
  BillLineItem,
  BillListRow,
  BillDetail,
  BillsListResponse,
  BillStats,
  ExtractPendingResponse,
} from '@quotech/bills';

// ---------------------------------------------------------------------------
// Query keys — exported so callers can invalidate from outside this file
// ---------------------------------------------------------------------------

export const BILLS_LIST_KEY = 'bills.list';
export const BILL_DETAIL_KEY = 'bills.detail';
export const BILL_STATS_KEY = 'bills.stats';

function useBillsInvalidator() {
  const { invalidate } = useInvalidateQueries();
  return (billId?: string) => {
    invalidate([BILLS_LIST_KEY]);
    invalidate([BILL_STATS_KEY]);
    if (billId) invalidate([BILL_DETAIL_KEY, billId]);
  };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useBills(filter: { status?: BillStatus | 'all' } = {}) {
  return useApiQuery<BillsListResponse>(
    [BILLS_LIST_KEY, filter],
    billsEndpoints.list(filter),
  );
}

export function useBill(billId: string | null | undefined) {
  return useApiQuery<BillDetail>(
    [BILL_DETAIL_KEY, billId],
    billId ? billsEndpoints.detail(billId) : '',
    { enabled: !!billId },
  );
}

export function useBillStats(opts: { pollMs?: number } = {}) {
  return useApiQuery<BillStats>(
    [BILL_STATS_KEY],
    billsEndpoints.stats(),
    opts.pollMs ? { refetchInterval: opts.pollMs } : undefined,
  );
}

// ---------------------------------------------------------------------------
// Mutations — raw useMutation because the URL is path-parameterised
// ---------------------------------------------------------------------------

interface UpdateBillVars {
  billId: string;
  patch: BillEditablePatch;
}

export function useUpdateBill() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<BillDetail, Error, UpdateBillVars>({
    mutationFn: async ({ billId, patch }) => {
      const { data } = await client.patch<BillDetail>(billsEndpoints.update(billId), patch);
      return data;
    },
    onSuccess: (_data, { billId }) => invalidateBills(billId),
  });
}

export function useApproveBill() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<BillDetail, Error, { billId: string }>({
    mutationFn: async ({ billId }) => {
      const { data } = await client.post<BillDetail>(billsEndpoints.approve(billId));
      return data;
    },
    onSuccess: (_data, { billId }) => invalidateBills(billId),
  });
}

export function useRejectBill() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<BillDetail, Error, { billId: string; reason?: string }>({
    mutationFn: async ({ billId, reason }) => {
      const { data } = await client.post<BillDetail>(billsEndpoints.reject(billId), {
        reason: reason ?? '',
      });
      return data;
    },
    onSuccess: (_data, { billId }) => invalidateBills(billId),
  });
}

export function useLinkBillToJob() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<BillDetail, Error, { billId: string; jobId: number | null }>({
    mutationFn: async ({ billId, jobId }) => {
      const { data } = await client.post<BillDetail>(billsEndpoints.linkJob(billId), {
        job_id: jobId,
      });
      return data;
    },
    onSuccess: (_data, { billId }) => invalidateBills(billId),
  });
}

export function useXeroPushBill() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<BillDetail, Error, { billId: string }>({
    mutationFn: async ({ billId }) => {
      const { data } = await client.post<BillDetail>(billsEndpoints.pushToXero(billId));
      return data;
    },
    onSuccess: (_data, { billId }) => invalidateBills(billId),
  });
}

export function useExtractBill() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<BillDetail, Error, { emailId: string }>({
    mutationFn: async ({ emailId }) => {
      const { data } = await client.post<BillDetail>(
        billsEndpoints.extractFromEmail(emailId),
      );
      return data;
    },
    onSuccess: () => invalidateBills(),
  });
}

export function useExtractPendingBills() {
  const client = useApiClient();
  const invalidateBills = useBillsInvalidator();
  return useMutation<ExtractPendingResponse, Error, { limit?: number; force?: boolean }>({
    mutationFn: async (vars) => {
      const { data } = await client.post<ExtractPendingResponse>(
        billsEndpoints.extractPending(),
        vars,
      );
      return data;
    },
    onSuccess: () => invalidateBills(),
  });
}
